import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Unlisted admin login route — an extra layer only, never the security control.
export const ADMIN_LOGIN_PATH = "/gt-console-9k2x7";

// Admin sessions are httpOnly Secure SameSite=Strict cookies — JavaScript
// cannot read the token (XSS-safe) and browsers never send it cross-site
// (CSRF-safe, reinforced server-side by the X-Requested-With header guard).
const client = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

export const adminGet = (path) => client.get(path).then((r) => r.data);
export const adminPost = (path, body, config) => client.post(path, body, config).then((r) => r.data);
export const adminPut = (path, body) => client.put(path, body).then((r) => r.data);
export const adminPatch = (path, body) => client.patch(path, body).then((r) => r.data);
export const adminDelete = (path) => client.delete(path).then((r) => r.data);

// Who is signed in — cached for the session so every admin page can gate
// role-restricted controls without depending on context from AdminLayout
// (context doesn't reach a page component that renders AdminLayout itself).
let _mePromise = null;
export const adminMe = () => {
  if (!_mePromise) {
    _mePromise = client.get("/admin/me").then((r) => r.data);
    _mePromise.catch(() => { _mePromise = null; }); // never cache a failed session check
  }
  return _mePromise;
};
export const adminLogout = () => client.post("/admin/logout").catch(() => {}).finally(() => { _mePromise = null; });
export const adminClient = client;

export const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

// Gold Time business timezone for Social Studio scheduling (S1C)
export const CHICAGO_TZ = "America/Chicago";
export const fmtChicago = (iso) =>
  iso ? new Intl.DateTimeFormat("en-US", { timeZone: CHICAGO_TZ, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(iso)) : "—";

export const apiError = (e) => {
  // Request never reached the backend (network down / request blocked / timed
  // out) — e.response is undefined. Name it so a silent no-op becomes visible.
  if (!e?.response) {
    if (e?.code === "ECONNABORTED") return "The request timed out before the server answered — it may not have started. Check your connection and try again.";
    return "The request never reached the server — nothing was started. Check your connection (or sign back in) and try again.";
  }
  const d = e.response.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return `Something went wrong (server error ${e.response.status}) — please try again.`;
};

// Authenticated file download (cookie session can't ride an <a href> across
// subdomains, so fetch as blob and save locally).
export const adminDownload = async (path, filename) => {
  const res = await adminClient.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Same, for POST endpoints (e.g. export-selected with a body).
export const adminDownloadPost = async (path, body, filename) => {
  const res = await adminClient.post(path, body, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
